/**
 * Getting better, slowly, and the ways it goes wrong (M4-04, AoD §10.3).
 *
 * The other three wound clocks are decided in minutes; this one is decided in
 * **days**, and that is the entire point of it. A spear wound taken on Saturday
 * is still stiff on Tuesday, and the continuity between two sessions a weekend
 * apart is what makes the MMO half of "MMO roguelike" emotionally real. Nothing
 * else in the game asks a player to come back and find out.
 *
 * Three commitments, each of which is easy to undo by accident:
 *
 * 1. **Healing is failable, and it is a roll against the wound.** Not a timer
 *    that runs down. A daily toughness check against what the wound still costs
 *    you (`wound.pain`), modified by the care it has had and the ground you are
 *    sleeping on. That means two identical wounds on two people diverge, which
 *    is what makes "how was your week" a question worth asking somebody.
 * 2. **The care states are what the treatment verbs already wrote.** `dirty`,
 *    `sutured`, `cauterized`, `dressedAt` — M4-01 and M4-02 set every one of
 *    them, and this module only reads. There is no second care model, and no
 *    number a treatment writes that only this file understands.
 * 3. **Nothing here is a multiplier on a timer.** Rest quality arrives as the
 *    same `REST_RECOVERY` ladder the blood regeneration uses (M3-06), converted
 *    into dice by one function, so "you do not heal shivering in the rain" is
 *    the *same* zero that stops blood coming back rather than a second rule that
 *    agrees with it today.
 *
 * Like `infection.ts`, this owns arithmetic and a roll over a `Vitals`, and
 * knows nothing about the world: `camp.ts` runs it on the day phase against the
 * ground somebody is standing on, `offline.ts` runs it across an absence, and
 * both hand it the same numbers.
 */
import { recomputeImpairment } from './wound.js';
import { TICKS_PER_GAME_DAY } from './time.js';
/* -------------------------------------------------------------------------- *
 * The roll
 * -------------------------------------------------------------------------- */
/** Sides on the die a day of healing is checked against. `INFECTION_DIE`'s twin. */
export const RECOVERY_DIE = 20;
/**
 * How far a tier multiplier moves the target, in dice.
 *
 * The tiers the rest of the game speaks in are multipliers — `REST_RECOVERY` is
 * ×0 to ×1.25, `nourishment` is ×0 to ×1 — and a d20 target cannot multiply. So
 * one function converts, and it converts *relative to 1*: the tier that means
 * "ordinary" (a fire, a full belly) is worth nothing either way, better is a
 * bonus and worse is a penalty. Four dice per whole multiple, so `dry` ground
 * (×0.5) costs 2 and the healer's bed M4-08 will bring (×2) is worth 4.
 *
 * A multiplier of **zero** is not a big penalty, it is a refusal: `tierModifier`
 * returns `null`, and the caller does not roll at all. That is the ticket's "wet
 * ground ×0 — no roll", and stating it as an absence of dice rather than as a
 * very bad target is what stops a lucky 1 from healing somebody in a bog.
 */
export const TIER_DICE = 4;
export function tierModifier(multiplier) {
    if (!Number.isFinite(multiplier) || multiplier <= 0)
        return null;
    return Math.round((multiplier - 1) * TIER_DICE);
}
/** What each damage type does to the healing roll (AoD §10.3, kept). */
export const TYPE_RECOVERY_MOD = {
    // A hole heals from the outside in, and what is at the bottom of it stays
    // warm and wet and out of the air.
    puncture: -1,
    bite: -1,
    // Bruising and broken bone: miserable, slow to be *useful* again, but there is
    // nothing open to go wrong.
    blunt: 1,
    cut: 0,
    burn: 0,
};
/** What each piece of care is worth on the daily roll (M4-01, M4-02's states). */
export const CARE_MODS = {
    /** Still carrying the filth it came with. Nothing knits under that. */
    dirty: -2,
    /** Washed out — the single best thing anybody can do for a wound. */
    clean: 1,
    /** Held shut by iron. Certain, ugly, and it does heal. */
    cauterized: 1,
    /** A dressing changed within the day (M4-02's rhythm). */
    dressed: 1,
    /** One left on past its day. Worse than none, which is why it is a rhythm. */
    lapsed: -1,
};
/**
 * Rolls a day gets: two for a sutured wound, one otherwise.
 *
 * This is where M4-02's promise that a suture "halves healing time" is actually
 * kept, and it is deliberately *rolls* rather than a bonus to the target. A
 * bonus would have made a sutured wound heal at a different rate; two rolls make
 * it heal in half the days, which is the sentence the ticket wrote and the one
 * `wound.ts` has been promising since M4-02.
 */
export const SUTURED_ROLLS = 2;
/**
 * The target a wound's daily roll has to come in under, or `null` for a wound
 * that does not roll today.
 *
 * `null` is returned for four separate reasons and they are all "there is no
 * healing to be had", not "the healing failed": the wound is done with, it is
 * infected (the track owns it until somebody resolves it — the ticket's "blocks
 * recovery entirely"), they are sleeping in the rain, or they are starving.
 * Distinguishing that from a failed roll matters, because a failed roll is a bad
 * day and this is a body that is not mending at all.
 */
export function recoveryTarget(wound, attributes, conditions) {
    if (wound.state === 'healed' || wound.state === 'permanent')
        return null;
    if (wound.state === 'infected')
        return null;
    if (wound.level <= 0)
        return null;
    // Still bleeding, so it has not begun to knit — and nothing that follows can
    // start it. This is the medicine ladder made load-bearing: pressure and a
    // dressing (M4-01) are not optional flavour before the "real" care, they are
    // the step that lets a body start healing at all. It is also what keeps
    // M3-07's promise intact across an absence: a man who logs out with an
    // unbound wound logs back in with it, because nobody was there to close it.
    if (wound.bleedRate > 0)
        return null;
    const restMod = tierModifier(conditions.rest);
    if (restMod === null)
        return null;
    const fedMod = tierModifier(conditions.fed ?? 1);
    if (fedMod === null)
        return null;
    let target = attributes.toughness - wound.pain + restMod + fedMod;
    target += TYPE_RECOVERY_MOD[wound.type] ?? 0;
    target += wound.dirty ? CARE_MODS.dirty : CARE_MODS.clean;
    if (wound.cauterized)
        target += CARE_MODS.cauterized;
    if (wound.dressedAt > 0) {
        target += dressingIsFresh(wound, conditions.tick) ? CARE_MODS.dressed : CARE_MODS.lapsed;
    }
    // Never certain and never hopeless. A wound nobody can heal would make the
    // grimmest possible reading of a bad camp — "this will never close" — into a
    // fact, and one that always healed would make care pointless.
    return Math.max(1, Math.min(RECOVERY_DIE - 1, target));
}
/**
 * Has the dressing been changed within the day?
 *
 * The same window `infection.ts` uses, deliberately duplicated as a comparison
 * rather than imported: `dressingLapsed` there additionally requires the wound
 * to be `treated`, because *infection* only cares about a dressing on a wound
 * that is otherwise closed. Healing cares about the dressing on any open wound.
 * Two questions that share a number and do not share an answer.
 */
function dressingIsFresh(wound, tick) {
    return tick - wound.dressedAt <= DRESSING_WINDOW;
}
/** A dressing wants changing daily — M4-02's rhythm, in this file's terms. */
const DRESSING_WINDOW = TICKS_PER_GAME_DAY;
/* -------------------------------------------------------------------------- *
 * The day
 * -------------------------------------------------------------------------- */
/**
 * One day of healing over every wound on a body.
 *
 * Wounds are walked in list order and a wound that cannot roll never touches the
 * dice, so the stream position after a call depends only on state — the same
 * replay discipline `rollInfections` keeps, and for the same reason.
 */
export function advanceRecovery(rng, vitals, attributes, conditions) {
    if (vitals.dead)
        return [];
    const events = [];
    for (const wound of vitals.wounds) {
        const target = recoveryTarget(wound, attributes, conditions);
        if (target === null)
            continue;
        const rolls = wound.sutured ? SUTURED_ROLLS : 1;
        for (let i = 0; i < rolls; i += 1) {
            if (wound.level <= 0)
                break;
            if (rng.roll(RECOVERY_DIE) > target)
                continue;
            events.push(...stepDown(wound, vitals, conditions));
        }
    }
    return events;
}
/** One level of mending, and the scar at the bottom of the ladder. */
function stepDown(wound, vitals, conditions) {
    const was = wound.level;
    const now = Math.max(0, was - 1);
    wound.level = now;
    // A wound that is mending is a wound that has stopped being an emergency: the
    // state moves up the ladder M4-02 finalised, and stays there until strain or
    // an infection takes it back.
    if (wound.state === 'fresh' || wound.state === 'treated')
        wound.state = 'healing';
    if (now === 0)
        return [...close(wound, vitals, conditions.tick)];
    const entry = conditions.entryFor?.(wound, now) ?? null;
    // What it costs now that it is smaller. From the table when there is one, and
    // proportionally when there is not — never left at the old number, because a
    // level-1 scratch that still cost four dice of pain would make healing
    // invisible to the only thing that measures it.
    wound.pain = entry ? entry.pain : Math.round((wound.pain * now) / Math.max(1, was));
    if (entry && wound.bleedRate > 0)
        wound.bleedRate = Math.min(wound.bleedRate, entry.bleedRate);
    return [{ type: 'mending', refs: [wound.id, wound.part, String(now)] }];
}
/** Level 0: it is a scar now, and the part gets its function back. */
function close(wound, vitals, tick) {
    wound.state = 'healed';
    wound.healedAt = tick;
    wound.bleedRate = 0;
    wound.pain = 0;
    wound.dirty = false;
    wound.infectionClock = 0;
    const part = vitals.body.parts[wound.part];
    // `recomputeImpairment` filters healed wounds out, so this is where a
    // half-crippled arm becomes an arm again — and where twenty scratches stop
    // costing anything, all at once, without anybody having remembered what each
    // of them took.
    if (part)
        recomputeImpairment(part, vitals.wounds, wound.part);
    return [{ type: 'healed', refs: [wound.id, wound.part, wound.type] }];
}
/* -------------------------------------------------------------------------- *
 * Strain (M4-04)
 * -------------------------------------------------------------------------- */
/** Below this level a healing wound is not something exertion can tear open. */
export const STRAIN_MIN_LEVEL = 2;
/**
 * Chance one burst of hard work tears a healing wound open, before toughness.
 *
 * Small, because it is rolled *often* — every committed swing and every heavy
 * job. At a level-3 wound and ordinary toughness it comes to roughly a quarter
 * over one ugly fight and about two thirds over three, which is the shape the
 * ticket wants: frightening, and still a gamble somebody might reasonably take.
 * Over a fight it comes to something a player can feel and plan around,
 * which is the number that matters: convalescence should be a state you can
 * *choose* to fight through, at a real risk, rather than one the interface
 * forbids. The wounded can walk, forage, keep camp and hold a spear if they must;
 * what they should not do is hold the shield-line, and this is why.
 */
export const STRAIN_CHANCE = 0.02;
/** Toughness at which strain is shrugged off entirely. Above human range. */
export const STRAIN_TOUGHNESS_SCALE = 40;
export function strainChance(wound, attributes) {
    if (wound.state !== 'healing')
        return 0;
    if (wound.level < STRAIN_MIN_LEVEL)
        return 0;
    const grip = Math.max(0, 1 - attributes.toughness / STRAIN_TOUGHNESS_SCALE);
    // Stitches hold better than a scab, and that is the last thing a suture buys.
    const closure = wound.sutured || wound.cauterized ? 0.5 : 1;
    return STRAIN_CHANCE * (wound.level - STRAIN_MIN_LEVEL + 1) * grip * closure;
}
/**
 * One burst of exertion, rolled against every healing wound.
 *
 * Called from the swing (`combat.ts`) and from the heavy camp jobs
 * (`interact.ts`), which is the honest reading of "per exertion burst": the
 * things that make a man grunt. Walking is not on the list, and neither is
 * treating somebody — a convalescent should be able to cross the valley and to
 * nurse a friend without gambling on their leg.
 */
export function strainWounds(rng, vitals, attributes, conditions) {
    if (vitals.dead)
        return [];
    const events = [];
    for (const wound of vitals.wounds) {
        const chance = strainChance(wound, attributes);
        if (chance <= 0)
            continue;
        if (!rng.chance(chance))
            continue;
        events.push(reopen(wound, conditions.entryFor?.(wound, wound.level) ?? null));
    }
    return events;
}
/**
 * Tear a healing wound back open (M4-04).
 *
 * It goes all the way back to `fresh` and it **bleeds**: the re-opened wound is
 * a live wound in every sense — staunchable, dressable, infectable — because a
 * half-state that looked like a wound and could not be treated like one would be
 * the most confusing thing in the medicine system. The stitches are gone, which
 * is the part that stings: somebody has to sew it again.
 */
export function reopen(wound, entry) {
    wound.state = 'fresh';
    wound.bleedRate = entry ? entry.bleedRate : wound.level * REOPEN_BLEED_PER_LEVEL;
    if (entry)
        wound.pain = entry.pain;
    wound.sutured = false;
    // The dressing came off with it. That resets the M4-02 rhythm too, which is
    // right: what is on the wound now is a torn dressing over a fresh bleed.
    wound.dressedAt = 0;
    return { type: 're-opened', refs: [wound.id, wound.part] };
}
/**
 * Blood per hour a re-opened wound gives up when there is no table to ask.
 *
 * The tables are the truth and the live rule set always passes one; this is the
 * engine's own content-free world, where a level-2 tear bleeding 4 an hour is a
 * defensible guess and a silent 0 would be a bug that looked like mercy.
 */
export const REOPEN_BLEED_PER_LEVEL = 2;
/**
 * One exertion by one actor, narrated (M4-04).
 *
 * The world-aware wrapper, and it lives here rather than in either of its two
 * callers so that "what counts as exertion" is one list in one file. A swing
 * (`combat.ts`) and a heavy camp job (`interact.ts`) both call exactly this, and
 * a third kind of hard work added later adds a line at its own call site rather
 * than a second copy of the rule.
 */
export function strainOnExertion(ctx, actor) {
    const combatant = ctx.world.combat[actor.id];
    if (!combatant)
        return;
    const table = ctx.catalogue.woundTable;
    const events = strainWounds(ctx.rng(`strain:${actor.id}`), combatant.vitals, combatant.loadout.attributes, {
        entryFor: (wound, level) => table && table.has(wound.type, wound.zone)
            ? table.resolve(wound.type, wound.zone, level)
            : null,
    });
    for (const event of events) {
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: [event.type, ...event.refs],
            // Witnesses, unlike the rest of recovery: a wound tearing open mid-fight
            // is a thing the people around you can see, and it is the moment somebody
            // decides to drag you out of the line.
            text: `${actor.name}'s wound tears open.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    }
}
//# sourceMappingURL=recovery.js.map